import { uniq } from 'lodash-es';
import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';

import client from 'apolloClient';
import FamiliesList from 'components/families/List';
import FamiliesShow from 'components/families/Show';
import { FONT_FACE_DECLARATIONS_QUERY } from './components/families/FONT_FACE_DECLARATIONS_QUERY';
import { FONT_FAMILY_QUERY } from './components/families/FONT_FAMILY_QUERY';
import { Layout } from './components/Layout';
import { NotFound } from './components/NotFound';
import {
  FontFaceDeclarationsQuery,
  FontFaceDeclarationsQueryVariables,
  FontFamilyQuery,
  FontFamilyQueryVariables,
} from './graphql/types/operations';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { getLanguage } from './utils/getLanguage';

export const router = createBrowserRouter([
  {
    Component: Layout,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        index: true,
        Component: FamiliesList,
      },
      {
        path: '*',
        Component: NotFound,
      },
      {
        path: 'families/:slug',
        loader: async ({ params }) => {
          const { slug } = params;
          const language = getLanguage();

          import('components/families/TypeTester');
          import('components/families/Purchase');

          if (slug != null) {
            const result = await client.query<
              FontFamilyQuery,
              FontFamilyQueryVariables
            >({
              query: FONT_FAMILY_QUERY,
              variables: { slug, language },
            });

            if (result.data.fontFamily != null) {
              client.query<
                FontFaceDeclarationsQuery,
                FontFaceDeclarationsQueryVariables
              >({
                query: FONT_FACE_DECLARATIONS_QUERY,
                variables: {
                  faceIds: uniq(
                    result.data.fontFamily.typeTesters.map(
                      ({ faceId }) => faceId,
                    ),
                  ).sort(),
                },
              });
            }
          }
        },
        Component: FamiliesShow,
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'checkout',
        children: [
          {
            path: 'cart',
            Component: lazy(() => import('components/checkout/Cart')),
            ErrorBoundary: RouteErrorBoundary,
          },
          {
            path: 'register',
            Component: lazy(() => import('components/checkout/Register')),
            ErrorBoundary: RouteErrorBoundary,
          },
          {
            path: 'payment',
            Component: lazy(() => import('components/checkout/Payment')),
            ErrorBoundary: RouteErrorBoundary,
          },
        ],
      },
      {
        path: 'orders/:orderNumber',
        Component: lazy(() => import('components/orders/Show')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'invoices/:invoiceSlug',
        Component: lazy(() => import('components/invoices/Show')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'sessions/new',
        Component: lazy(() => import('components/sessions/New')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'passwords/new',
        Component: lazy(() => import('components/passwords/New')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'passwords/:token',
        Component: lazy(() => import('components/passwords/Edit')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'about',
        Component: lazy(() => import('components/pages/About')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'faq',
        Component: lazy(
          () => import('components/pages/FrequentlyAskedQuestions'),
        ),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'our-fonts-in-use',
        Component: lazy(() => import('components/pages/InUse')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'licensing',
        Component: lazy(() => import('components/pages/Licensing')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'terms-and-conditions',
        Component: lazy(() => import('components/pages/TermsAndConditions')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'invoice-terms-and-conditions',
        Component: lazy(
          () => import('components/pages/InvoiceTermsAndConditions'),
        ),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'privacy-policy',
        Component: lazy(() => import('components/pages/PrivacyPolicy')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'imprint',
        Component: lazy(() => import('components/pages/Imprint')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'returns-policy',
        Component: lazy(() => import('components/pages/ReturnsPolicy')),
        ErrorBoundary: RouteErrorBoundary,
      },
      {
        path: 'account',
        Component: lazy(() => import('components/Account')),
        ErrorBoundary: RouteErrorBoundary,
      },
    ],
  },
]);
